import * as THREE from 'three';
import type { Subscription } from 'rxjs';
import type {
    SectionViewControllerHost,
    SectionViewDocumentSnapshot,
    SectionViewOffsetMethod,
    SectionViewPlaneDefinitionSnapshot,
    SectionViewPlaneDraft,
    SectionViewPlaneIndex,
    SectionViewReferenceSnapshot,
    SectionViewRenderPort,
    SectionViewRotationHandleDragEvent,
    SectionViewSessionSnapshot,
    SectionViewStandardPlaneKind,
    SectionViewStateChangedListener,
    SectionViewStateSnapshot,
} from '../../types/section-view/SectionViewTypes';
import { EntityInteractionEventType } from '../manager/EntitySelectionManager';
import {
    PatternDirectionArrowAxisDragPhase,
    type PatternDirectionArrowAxisDragEvent,
} from '../manager/directionArrow';
import { ReferencePlane } from '../reference/ReferencePlane';
import type { SketchPlanarFaceSelection } from '../sketch/face/SketchPlanarFaceSelection';
import { createPuppySectionViewExtension } from '../export/puppy/puppySectionViewExtension';
import { SectionPlane } from './SectionPlane';
import { SectionViewVisibilityPolicy } from './SectionViewVisibilityPolicy';

const SECTION_PLANE_INDEXES = [0, 1, 2] as const;
const SECTION_COLORS = ['#f4df4e', '#74c0fc', '#ff8fab'] as const;
const SECTION_ARROW_SLOT_PREFIX = 'section-view:plane:';
const SECTION_ARROW_DRAG_CONTEXT_PREFIX = 'section-view:offset:';
const SECTION_PREVIEW_ARROW_COLOR_FALLBACK = 0xf4df4e;
/**
 * 剖面平移箭头相对旋转环世界半径的视觉比例。
 *
 * 这些比例保留原有 48 像素环与各箭头部件的造型关系，
 * 但运行时只使用会话开始时冻结的世界半径。
 */
const SECTION_PREVIEW_GIZMO_WORLD_RATIOS = {
    shaftRadius: 3 / 48,
    bottomLength: 28 / 48,
    topLength: 62 / 48,
    headRadius: 7 / 48,
    headLength: 18 / 48,
} as const;
const HEX_COLOR_PATTERN = /^#[0-9a-fA-F]{6}$/;

/**
 * SOLIDWORKS 风格剖面视图应用控制器。
 *
 * 职责与生命周期：
 * 1. 统一管理打开、草稿预览、确认、取消、完整视图切换和文档恢复；
 * 2. 把实体面选择转换成稳定世界参考，并驱动纯渲染端口；
 * 3. 不修改 B-Rep，也不写入建模 Undo/Redo；取消通过会话快照回滚视图草稿；
 * 4. `dispose()` 负责结束选择/拖拽订阅、清理自身箭头并释放渲染端口。
 */
export class SectionViewController {
    /** Viewer 最小宿主端口。 */
    private readonly host: SectionViewControllerHost;
    /** 剖面视图渲染端口。 */
    private readonly renderPort: SectionViewRenderPort;
    /** 固定三个瞬态数学平面。 */
    private readonly sectionPlanes = SECTION_PLANE_INDEXES.map((index) => new SectionPlane(index));
    /** GPU 裁剪之外的 Raycaster 可见性策略。 */
    private readonly visibilityPolicy = new SectionViewVisibilityPolicy();
    /** 面参考坐标系解析器；负数 ID 避免污染正式基准面序号。 */
    private readonly referenceFrameResolver = new ReferencePlane(
        '剖面参考坐标系解析器',
        new THREE.Vector3(0, 0, 1),
        0,
        {},
        null,
        -1000,
    );
    /** 当前三个槽位草稿。 */
    private sections: SectionViewPlaneDraft[] = this.createInitialSections();
    /** 状态变化监听集合。 */
    private readonly stateChangedListeners = new Set<SectionViewStateChangedListener>();
    /** 渲染同步复用的活动平面数组。 */
    private readonly activePlaneBuffer: SectionPlane[] = [];
    /** 渲染同步复用的颜色数组。 */
    private readonly activeColorBuffer: string[] = [];
    /** 当前实际创建的预览箭头槽位，避免对空槽位重复触发清理。 */
    private readonly previewArrowSlots = new Set<string>();
    /** 当前实际显示的旋转手柄槽位，避免对空槽位重复触发清理。 */
    private readonly previewRotationHandleSlots = new Set<SectionViewPlaneIndex>();
    /** 三个槽位本轮是否成功生成活动数学平面。 */
    private readonly activePlaneSlots = [false, false, false];
    /** 三个剖面箭头的原点复用缓存，避免拖拽同步时创建 Vector3。 */
    private readonly previewArrowAnchorBuffers = SECTION_PLANE_INDEXES.map(() => new THREE.Vector3());
    /** 三个剖面最终显示 X 轴的复用缓存。 */
    private readonly previewDisplayXAxisBuffers = SECTION_PLANE_INDEXES.map(() => new THREE.Vector3());
    /** 三个剖面最终显示 Y 轴的复用缓存。 */
    private readonly previewDisplayYAxisBuffers = SECTION_PLANE_INDEXES.map(() => new THREE.Vector3());
    /** 三个剖面最终显示法向的复用缓存。 */
    private readonly previewDisplayNormalBuffers = SECTION_PLANE_INDEXES.map(() => new THREE.Vector3());
    /** 三个剖面 X 属性角求值轴的复用缓存。 */
    private readonly previewRotationXAxisBuffers = SECTION_PLANE_INDEXES.map(() => new THREE.Vector3());
    /** 三个剖面 Y 属性角求值轴的复用缓存。 */
    private readonly previewRotationYAxisBuffers = SECTION_PLANE_INDEXES.map(() => new THREE.Vector3());
    /** 打开编辑会话前的回滚快照。 */
    private sessionSnapshot: SectionViewSessionSnapshot | null = null;
    /** 当前编辑会话内冻结的旋转环世界半径。 */
    private previewGizmoRingRadiusWorld: number | null = null;
    /** 编辑前选择模式。 */
    private previousSelectionMode: ReturnType<SectionViewControllerHost['getSelectionMode']> | null = null;
    /** 实体选择更新订阅。 */
    private selectionUpdatedSubscription: Subscription | null = null;
    /** 方向箭头拖拽订阅。 */
    private arrowDragSubscription: Subscription | null = null;
    /** 双旋转手柄拖拽订阅。 */
    private rotationHandleDragSubscription: Subscription | null = null;
    /** 剖切显示是否启用。 */
    private isEnabled = false;
    /** 是否处于编辑会话。 */
    private isEditing = false;
    /** 是否显示参考面和方向箭头预览。 */
    private isPreview = true;
    /** 当前活动槽位。 */
    private activeSectionIndex: SectionViewPlaneIndex = 0;
    /** 是否显示视觉剖面盖。 */
    private showCap = true;
    /** 偏移方向计算方法。 */
    private offsetMethod: SectionViewOffsetMethod = 'reference-plane';
    /** 控制器是否已经释放。 */
    private isDisposed = false;

    /**
     * 创建剖面视图控制器。
     *
     * @param host Viewer 最小宿主端口。
     * @param renderPort 剖面视图渲染端口。
     */
    public constructor(host: SectionViewControllerHost, renderPort: SectionViewRenderPort) {
        this.host = host;
        this.renderPort = renderPort;
        this.arrowDragSubscription = this.host.patternDirectionArrowManager
            .observeAxisDrag()
            .subscribe((event) => this.handleArrowDrag(event));
        this.rotationHandleDragSubscription = this.host.sectionViewRotationHandleManager
            .observeRotationDrag()
            .subscribe((event) => this.handleRotationHandleDrag(event));
        this.syncRendering();
    }

    /**
     * 读取当前普通对象状态快照。
     *
     * @returns 与 Three.js 对象隔离的只读快照。
     */
    public getState(): SectionViewStateSnapshot {
        const hasDefinition = this.hasDefinition();
        return {
            isEnabled: this.isEnabled,
            isEditing: this.isEditing,
            hasDefinition,
            canConfirm: this.canConfirm(),
            isPreview: this.isPreview,
            activeSectionIndex: this.activeSectionIndex,
            showCap: this.showCap,
            offsetMethod: this.offsetMethod,
            sections: this.sections.map((section) => ({
                index: section.index,
                enabled: section.enabled,
                hasReference: section.reference !== null,
                referenceLabel: section.reference?.label ?? '',
                reverse: section.reverse,
                offsetMillimeters: section.offsetMillimeters,
                rotationXDegrees: section.rotationXDegrees,
                rotationYDegrees: section.rotationYDegrees,
                color: section.color,
                errorMessage: section.errorMessage,
            })),
        };
    }

    /**
     * 订阅控制器状态变化。
     *
     * @param listener 状态变化回调。
     * @returns 取消订阅函数。
     */
    public subscribeStateChanged(listener: SectionViewStateChangedListener): () => void {
        this.stateChangedListeners.add(listener);
        return () => {
            this.stateChangedListeners.delete(listener);
        };
    }

    /**
     * 打开剖面视图编辑会话。
     *
     * 首次使用时优先吸收预选平面面，无预选时确定性采用前视基准面；
     * 已有定义的重新编辑不读取当前选择，避免静默替换已确认参考。
     *
     * @returns 无返回值。
     */
    public open(): void {
        this.assertNotDisposed();
        if (this.isEditing) {
            return;
        }

        const hasExistingDefinition = this.hasDefinition();
        // 预选面必须在切换选择模式前读取，否则会被 EntitySelectionManager 清空。
        const preselectedFace = hasExistingDefinition
            ? null
            : this.host.getSelectedPlanarFace();
        this.sessionSnapshot = this.createSessionSnapshot();
        this.previousSelectionMode = this.host.getSelectionMode();
        // 每次新会话只在第一次显示手柄时标定世界半径，后续相机变化不再参与。
        this.previewGizmoRingRadiusWorld = null;
        this.isEditing = true;
        this.isPreview = true;

        if (!hasExistingDefinition) {
            if (preselectedFace) {
                this.assignPlanarFaceReference(this.activeSectionIndex, preselectedFace);
            }
            if (!this.sections[0].reference) {
                // 非平面或退化法向的预选不能留下无定义草稿，统一回退到 SOLIDWORKS 首期默认面。
                this.sections[0].reference = this.createStandardPlaneReference('front');
                this.sections[0].errorMessage = null;
                this.activeSectionIndex = 0;
            }
        }
        this.isEnabled = this.hasDefinition();
        this.host.setSelectionMode('face', { preserveSelection: false });
        this.startSelectionSubscription();
        this.syncAndPublish();
    }

    /**
     * 切换普通可编辑剖面命令。
     *
     * 用户从普通工具栏退出时，已确认定义与当前草稿会一起清空；下一次点击必定进入
     * 全新的选择会话。只读历史预览若只需显隐既有定义，应调用 `toggleVisibility()`。
     *
     * @returns 无返回值。
     */
    public toggle(): void {
        this.assertNotDisposed();
        const hasDefinition = this.hasDefinition();
        if (this.isEditing || (hasDefinition && this.isEnabled)) {
            this.resetDefinitionState();
            this.syncAndPublish();
            return;
        }

        if (hasDefinition) {
            // 禁用的持久化定义不能被普通工具栏静默复用，先清空再直接开始全新会话。
            this.resetDefinitionState();
        }
        this.open();
    }

    /**
     * 仅切换已恢复剖面定义的可见性。
     *
     * 该入口只供只读历史预览使用，不进入编辑会话，也不会清空持久化定义。
     *
     * @returns 无返回值；没有定义时安全忽略。
     * @throws 当前处于编辑会话或控制器已释放时抛出错误。
     */
    public toggleVisibility(): void {
        this.assertNotDisposed();
        if (this.isEditing) {
            throw new Error('编辑剖面视图时不能使用只读显隐开关');
        }
        if (!this.hasDefinition()) {
            return;
        }
        this.isEnabled = !this.isEnabled;
        this.syncAndPublish();
    }

    /**
     * 重新编辑现有剖面；没有定义时等价于首次打开。
     *
     * @returns 无返回值。
     */
    public edit(): void {
        this.open();
    }

    /**
     * 确认草稿并保留剖切显示。
     *
     * @returns 无返回值。
     * @throws 当活动剖面缺少参考或依赖关系无效时。
     */
    public confirm(): void {
        this.assertEditing();
        if (!this.canConfirm()) {
            throw new Error('剖面视图草稿无效：所有启用剖面都必须具有合法参考');
        }
        this.isEditing = false;
        this.isPreview = false;
        this.isEnabled = true;
        this.sessionSnapshot = null;
        this.stopSelectionSubscriptionAndRestoreMode();
        this.syncAndPublish();
    }

    /**
     * 取消当前编辑，并原子恢复打开前的视图状态。
     *
     * @returns 无返回值。
     * @throws 当前不处于编辑会话或控制器已释放时抛出错误。
     */
    public cancel(): void {
        this.assertEditing();
        if (this.sessionSnapshot) {
            this.restoreSessionSnapshot(this.sessionSnapshot);
        }
        this.sessionSnapshot = null;
        this.isEditing = false;
        this.stopSelectionSubscriptionAndRestoreMode();
        this.syncAndPublish();
    }

    /**
     * 设置当前活动剖面。
     *
     * @param index 零基槽位。
     * @returns 无返回值。
     * @throws 不在编辑会话或槽位超出 0 至 2 时抛出错误。
     */
    public setActiveSection(index: number): void {
        this.assertEditing();
        this.activeSectionIndex = this.requireSectionIndex(index);
        this.syncAndPublish();
    }

    /**
     * 启用或关闭第二/第三剖面。
     *
     * @param index 零基槽位。
     * @param enabled 是否启用。
     * @returns 无返回值。
     * @throws 尝试关闭基础剖面、跳过第二剖面启用第三剖面，或槽位无效时抛出错误。
     */
    public setSectionEnabled(index: number, enabled: boolean): void {
        this.assertEditing();
        const sectionIndex = this.requireSectionIndex(index);
        if (sectionIndex === 0 && !enabled) {
            throw new Error('剖面 1 是平面剖的基础剖面，不能关闭');
        }
        if (sectionIndex === 2 && enabled && !this.sections[1].enabled) {
            throw new Error('剖面 3 依赖剖面 2，请先启用剖面 2');
        }
        this.sections[sectionIndex].enabled = enabled;
        this.sections[sectionIndex].errorMessage = null;
        if (sectionIndex === 1 && !enabled) {
            this.sections[2].enabled = false;
        }
        if (enabled) {
            this.activeSectionIndex = sectionIndex;
        }
        this.isEnabled = this.hasDefinition();
        this.syncAndPublish();
    }

    /**
     * 选择内置前/上/右视基准面。
     *
     * @param index 零基槽位。
     * @param kind 标准面类型。
     * @returns 无返回值。
     * @throws 槽位无效、未启用，或当前不在编辑会话时抛出错误。
     */
    public selectStandardPlane(index: number, kind: SectionViewStandardPlaneKind): void {
        this.assertEditing();
        const sectionIndex = this.requireSectionIndex(index);
        const section = this.sections[sectionIndex];
        if (!section.enabled) {
            throw new Error(`剖面 ${sectionIndex + 1} 尚未启用`);
        }
        section.reference = this.createStandardPlaneReference(kind);
        section.errorMessage = null;
        this.activeSectionIndex = sectionIndex;
        this.isEnabled = true;
        this.syncAndPublish();
    }

    /**
     * 捕获当前选中平面实体面。
     *
     * @param index 零基槽位。
     * @returns 无返回值；无有效选择时在对应槽位写入校验错误。
     * @throws 槽位无效或当前不在编辑会话时抛出错误。
     */
    public captureCurrentSelection(index: number): void {
        this.assertEditing();
        const sectionIndex = this.requireSectionIndex(index);
        const face = this.host.getSelectedPlanarFace();
        if (!face) {
            this.sections[sectionIndex].errorMessage = '请选择一个平面实体面';
            this.publishStateChanged();
            return;
        }
        this.assignPlanarFaceReference(sectionIndex, face);
        this.activeSectionIndex = sectionIndex;
        this.isEnabled = true;
        this.syncAndPublish();
    }

    /**
     * 设置指定剖面的反向状态。
     *
     * @param index 零基槽位。
     * @param reverse 是否反转保留半空间。
     * @returns 无返回值。
     */
    public setReverse(index: number, reverse: boolean): void {
        this.updateSectionBoolean(index, 'reverse', reverse);
    }

    /**
     * 设置指定剖面的毫米偏移。
     *
     * @param index 零基槽位。
     * @param value 有限的偏移量，单位为毫米。
     * @returns 无返回值。
     */
    public setOffsetMillimeters(index: number, value: number): void {
        this.updateSectionNumber(index, 'offsetMillimeters', value);
    }

    /**
     * 设置指定剖面绕局部 X 轴的旋转角度。
     *
     * @param index 零基槽位。
     * @param value 有限的旋转角度，单位为度。
     * @returns 无返回值。
     */
    public setRotationXDegrees(index: number, value: number): void {
        this.updateSectionNumber(index, 'rotationXDegrees', value);
    }

    /**
     * 设置指定剖面绕局部 Y 轴的旋转角度。
     *
     * @param index 零基槽位。
     * @param value 有限的旋转角度，单位为度。
     * @returns 无返回值。
     */
    public setRotationYDegrees(index: number, value: number): void {
        this.updateSectionNumber(index, 'rotationYDegrees', value);
    }

    /**
     * 设置指定剖面颜色。
     *
     * @param index 零基槽位。
     * @param color CSS 十六进制颜色。
     * @returns 无返回值。
     * @throws 颜色不是 `#RRGGBB` 格式、槽位无效或不在编辑会话时抛出错误。
     */
    public setColor(index: number, color: string): void {
        this.assertEditing();
        const sectionIndex = this.requireSectionIndex(index);
        if (!HEX_COLOR_PATTERN.test(color)) {
            throw new Error('剖面颜色必须是 #RRGGBB 格式');
        }
        this.sections[sectionIndex].color = color.toLowerCase();
        this.syncAndPublish();
    }

    /**
     * 设置偏移方向计算方法。
     *
     * @param method 参考基准面或所选基准面方法。
     * @returns 无返回值。
     * @throws 方法值不受支持或不在编辑会话时抛出错误。
     */
    public setOffsetMethod(method: SectionViewOffsetMethod): void {
        this.assertEditing();
        if (method !== 'reference-plane' && method !== 'selected-plane') {
            throw new Error(`不支持的剖面视图偏移方法: ${String(method)}`);
        }
        this.offsetMethod = method;
        this.syncAndPublish();
    }

    /**
     * 设置视觉剖面盖状态。
     *
     * @param show 是否显示剖面盖。
     * @returns 无返回值。
     */
    public setShowCap(show: boolean): void {
        this.assertEditing();
        this.showCap = show;
        this.syncAndPublish();
    }

    /**
     * 设置编辑预览状态。
     *
     * @param show 是否显示剖切面与方向箭头。
     * @returns 无返回值。
     */
    public setPreview(show: boolean): void {
        this.assertEditing();
        this.isPreview = show;
        this.syncAndPublish();
    }

    /**
     * 创建当前文档持久化快照。
     *
     * PropertyManager 编辑期间只序列化会话前的 confirmed 快照，
     * 未确认草稿不得泄漏到离线包或其它文档持久化入口。
     *
     * @returns 有已确认定义时返回普通数据快照，否则返回 undefined。
     */
    public createDocumentSnapshot(): SectionViewDocumentSnapshot | undefined {
        const confirmedSessionSnapshot = this.isEditing ? this.sessionSnapshot : null;
        const snapshotSections = confirmedSessionSnapshot?.sections ?? this.sections;
        const sections = snapshotSections
            .filter((section) => section.reference !== null)
            .map((section) => this.createPlaneDefinitionSnapshot(section));
        if (sections.length === 0) {
            return undefined;
        }
        return {
            enabled: confirmedSessionSnapshot?.isEnabled ?? this.isEnabled,
            showCap: confirmedSessionSnapshot?.showCap ?? this.showCap,
            offsetMethod: confirmedSessionSnapshot?.offsetMethod ?? this.offsetMethod,
            sections,
        };
    }

    /**
     * 从离线包恢复文档级剖面视图。
     *
     * @param snapshot 已解析或 SDK 调用方传入的快照；空值表示清空定义。
     * @returns 无返回值。
     * @throws 快照违反版本化剖面协议或控制器已释放时抛出错误。
     */
    public restoreDocumentSnapshot(snapshot: SectionViewDocumentSnapshot | null | undefined): void {
        this.assertNotDisposed();
        this.resetDefinitionState();

        if (!snapshot) {
            this.syncAndPublish();
            return;
        }

        // 统一复用离线包校验器，避免 SDK 恢复入口与文件导入形成两套边界规则。
        const normalized = createPuppySectionViewExtension(snapshot)?.snapshot;
        if (!normalized) {
            throw new Error('剖面视图恢复快照必须包含至少一个剖面定义');
        }
        normalized.sections.forEach((definition) => {
            const section = this.sections[definition.index];
            section.enabled = definition.enabled;
            section.reference = this.cloneReference(definition.reference);
            section.reverse = definition.reverse;
            section.offsetMillimeters = definition.offsetMillimeters;
            section.rotationXDegrees = definition.rotationXDegrees;
            section.rotationYDegrees = definition.rotationYDegrees;
            section.color = definition.color;
            section.errorMessage = null;
        });
        this.isEnabled = normalized.enabled;
        this.showCap = normalized.showCap;
        this.offsetMethod = normalized.offsetMethod;
        this.activeSectionIndex = normalized.sections[normalized.sections.length - 1]?.index ?? 0;
        this.syncAndPublish();
    }

    /**
     * 判断 Raycaster 世界命中点是否位于剖切后的可见区域。
     *
     * @param worldPoint 世界空间命中点。
     * @returns 是否允许进入选择链路。
     */
    public isPointVisible(worldPoint: THREE.Vector3): boolean {
        return this.visibilityPolicy.isPointVisible(worldPoint);
    }

    /**
     * 判断当前是否存在实际生效的剖面视图。
     *
     * 该入口供高频拾取链读取，避免通过 `getState()` 创建完整 UI 快照。
     *
     * @returns 剖切显示已开启且至少有一个有效剖面时返回 true。
     */
    public isActive(): boolean {
        return this.isEnabled && this.hasDefinition();
    }

    /**
     * 通知渲染层可剖切模型内容已经发生变化。
     *
     * 通知前先按稳定面身份刷新世界坐标系；控制器已释放时安全忽略。
     *
     * @returns 无返回值。
     */
    public notifySectionableContentChanged(): void {
        if (!this.isDisposed) {
            this.refreshPlanarFaceReferences();
            this.syncAndPublish();
            this.renderPort.notifyContentChanged();
        }
    }

    /**
     * 释放控制器持有的全部订阅、箭头与渲染资源。
     *
     * 本方法可重入；释放后不得再打开、恢复或修改剖面状态。
     *
     * @returns 无返回值。
     */
    public dispose(): void {
        if (this.isDisposed) {
            return;
        }
        if (this.isEditing) {
            this.stopSelectionSubscriptionAndRestoreMode();
        } else {
            this.selectionUpdatedSubscription?.unsubscribe();
            this.selectionUpdatedSubscription = null;
        }
        this.arrowDragSubscription?.unsubscribe();
        this.arrowDragSubscription = null;
        this.rotationHandleDragSubscription?.unsubscribe();
        this.rotationHandleDragSubscription = null;
        SECTION_PLANE_INDEXES.forEach((index) => {
            this.host.patternDirectionArrowManager.clearArrow(this.createArrowSlot(index));
            this.host.sectionViewRotationHandleManager.clearHandle(index);
        });
        this.previewArrowSlots.clear();
        this.previewRotationHandleSlots.clear();
        this.visibilityPolicy.clear();
        this.stateChangedListeners.clear();
        this.renderPort.dispose();
        this.isDisposed = true;
    }

    /**
     * 创建固定三个槽位的初始草稿。
     *
     * @returns 仅启用第一槽、尚无参考的全新草稿数组。
     */
    private createInitialSections(): SectionViewPlaneDraft[] {
        return SECTION_PLANE_INDEXES.map((index) => ({
            index,
            enabled: index === 0,
            reference: null,
            reverse: false,
            offsetMillimeters: 0,
            rotationXDegrees: 0,
            rotationYDegrees: 0,
            color: SECTION_COLORS[index],
            errorMessage: null,
        }));
    }

    /**
     * 创建打开会话前的深拷贝回滚快照。
     *
     * @returns 与当前可变草稿隔离的会话快照。
     */
    private createSessionSnapshot(): SectionViewSessionSnapshot {
        return {
            isEnabled: this.isEnabled,
            isPreview: this.isPreview,
            activeSectionIndex: this.activeSectionIndex,
            showCap: this.showCap,
            offsetMethod: this.offsetMethod,
            sections: this.sections.map((section) => this.cloneSectionDraft(section)),
        };
    }

    /**
     * 恢复编辑会话前快照。
     *
     * @param snapshot 打开当前会话时捕获的深拷贝快照。
     * @returns 无返回值。
     */
    private restoreSessionSnapshot(snapshot: SectionViewSessionSnapshot): void {
        this.isEnabled = snapshot.isEnabled;
        this.isPreview = snapshot.isPreview;
        this.activeSectionIndex = snapshot.activeSectionIndex;
        this.showCap = snapshot.showCap;
        this.offsetMethod = snapshot.offsetMethod;
        this.sections = snapshot.sections.map((section) => this.cloneSectionDraft(section));
    }

    /**
     * 原子恢复没有剖面定义的默认状态。
     *
     * 本方法只重置内存与交互生命周期，不触发渲染或状态发布；调用方可据业务意图选择
     * 立即进入新会话或一次性同步空状态，避免出现中间帧和属性栏闪烁。
     *
     * @returns 无返回值。
     */
    private resetDefinitionState(): void {
        this.stopSelectionSubscriptionAndRestoreMode();
        this.sessionSnapshot = null;
        this.isEditing = false;
        this.isEnabled = false;
        this.isPreview = false;
        this.activeSectionIndex = 0;
        this.showCap = true;
        this.offsetMethod = 'reference-plane';
        this.sections = this.createInitialSections();
    }

    /**
     * 深拷贝单个可变草稿。
     *
     * @param section 待复制的剖面草稿。
     * @returns 参考向量也已隔离的草稿副本。
     */
    private cloneSectionDraft(section: SectionViewPlaneDraft): SectionViewPlaneDraft {
        return {
            ...section,
            reference: section.reference ? this.cloneReference(section.reference) : null,
        };
    }

    /**
     * 深拷贝可序列化参考。
     *
     * @param reference 待复制的稳定参考。
     * @returns 四组坐标向量均已复制的参考副本。
     */
    private cloneReference(reference: SectionViewReferenceSnapshot): SectionViewReferenceSnapshot {
        return {
            ...reference,
            origin: [...reference.origin],
            normal: [...reference.normal],
            xAxis: [...reference.xAxis],
            yAxis: [...reference.yAxis],
        };
    }

    /**
     * 创建单个可持久化剖面定义。
     *
     * @param section 已具有稳定参考的剖面草稿。
     * @returns 不含 Three.js 实例的普通数据定义。
     * @throws 草稿缺少参考时抛出错误。
     */
    private createPlaneDefinitionSnapshot(
        section: SectionViewPlaneDraft,
    ): SectionViewPlaneDefinitionSnapshot {
        if (!section.reference) {
            throw new Error(`剖面 ${section.index + 1} 缺少参考，无法创建文档快照`);
        }
        return {
            index: section.index,
            enabled: section.enabled,
            reference: this.cloneReference(section.reference),
            reverse: section.reverse,
            offsetMillimeters: section.offsetMillimeters,
            rotationXDegrees: section.rotationXDegrees,
            rotationYDegrees: section.rotationYDegrees,
            color: section.color,
        };
    }

    /**
     * 把当前选中平面面转换为稳定参考。
     *
     * @param index 目标剖面槽位。
     * @param face 统一解析后的平面面选择。
     * @returns 无返回值；无效平面会在草稿中记录错误。
     */
    private assignPlanarFaceReference(
        index: SectionViewPlaneIndex,
        face: SketchPlanarFaceSelection,
    ): void {
        if (
            !face.isPlanar
            || !Number.isFinite(face.worldPlaneNormal.x)
            || !Number.isFinite(face.worldPlaneNormal.y)
            || !Number.isFinite(face.worldPlaneNormal.z)
            || face.worldPlaneNormal.lengthSq() <= 1e-12
        ) {
            this.sections[index].errorMessage = '所选实体面不是有效平面';
            return;
        }
        const origin = face.worldRepresentativePoint.lengthSq() > 0
            ? face.worldRepresentativePoint
            : face.worldPlanePoint;
        this.referenceFrameResolver.updateReferenceFrame(origin, face.worldPlaneNormal);
        const xAxis = this.referenceFrameResolver.xAxis;
        const yAxis = this.referenceFrameResolver.yAxis;
        const normal = face.worldPlaneNormal.clone().normalize();
        this.sections[index].reference = {
            kind: 'planar-face',
            label: `面 ${face.faceIndex}`,
            objectId: face.objectId,
            faceIndex: face.faceIndex,
            origin: [origin.x, origin.y, origin.z],
            normal: [normal.x, normal.y, normal.z],
            xAxis: [xAxis.x, xAxis.y, xAxis.z],
            yAxis: [yAxis.x, yAxis.y, yAxis.z],
        };
        this.sections[index].errorMessage = null;
    }

    /**
     * 根据稳定 objectId/faceIndex 刷新实体面世界坐标系。
     *
     * 模型重载或装配位姿变化后优先使用当前拓扑重新绑定；解析失败时保留持久化世界坐标
     * 回退，确保离线包仍可显示已保存剖面，而不会因拓扑暂未就绪直接丢失定义。
     *
     * @returns 无返回值。
     */
    private refreshPlanarFaceReferences(): void {
        if (!this.host.resolveSectionPlanarFaceReference) {
            return;
        }
        this.sections.forEach((section) => {
            const reference = section.reference;
            if (reference?.kind !== 'planar-face'
                || !reference.objectId
                || !Number.isInteger(reference.faceIndex)) {
                return;
            }
            const face = this.host.resolveSectionPlanarFaceReference!(
                reference.objectId,
                reference.faceIndex!,
            );
            if (face?.isPlanar) {
                this.assignPlanarFaceReference(section.index, face);
            }
        });
    }

    /**
     * 创建内置标准面的稳定坐标系快照。
     *
     * @param kind 前视、上视或右视基准面。
     * @returns 世界原点与正交坐标轴的可序列化快照。
     */
    private createStandardPlaneReference(kind: SectionViewStandardPlaneKind): SectionViewReferenceSnapshot {
        switch (kind) {
            case 'top':
                return {
                    kind: 'standard-plane',
                    label: '上视基准面',
                    origin: [0, 0, 0],
                    normal: [0, 1, 0],
                    xAxis: [1, 0, 0],
                    yAxis: [0, 0, -1],
                };
            case 'right':
                return {
                    kind: 'standard-plane',
                    label: '右视基准面',
                    origin: [0, 0, 0],
                    normal: [1, 0, 0],
                    xAxis: [0, 0, -1],
                    yAxis: [0, 1, 0],
                };
            default:
                return {
                    kind: 'standard-plane',
                    label: '前视基准面',
                    origin: [0, 0, 0],
                    normal: [0, 0, 1],
                    xAxis: [1, 0, 0],
                    yAxis: [0, 1, 0],
                };
        }
    }

    /**
     * 启动实体选择更新订阅。
     *
     * 重复启动前会先取消旧订阅，订阅生命周期不超过当前编辑会话。
     *
     * @returns 无返回值。
     */
    private startSelectionSubscription(): void {
        this.selectionUpdatedSubscription?.unsubscribe();
        this.selectionUpdatedSubscription = this.host
            .observeEntityInteraction(EntityInteractionEventType.SelectionUpdated)
            ?.subscribe(() => {
                const face = this.host.getSelectedPlanarFace();
                if (!face || !this.isEditing || !this.sections[this.activeSectionIndex].enabled) {
                    return;
                }
                this.assignPlanarFaceReference(this.activeSectionIndex, face);
                this.isEnabled = true;
                this.syncAndPublish();
            }) ?? null;
    }

    /**
     * 停止选择订阅并恢复进入命令前的选择模式。
     *
     * @returns 无返回值。
     */
    private stopSelectionSubscriptionAndRestoreMode(): void {
        this.selectionUpdatedSubscription?.unsubscribe();
        this.selectionUpdatedSubscription = null;
        if (this.previousSelectionMode) {
            this.host.setSelectionMode(this.previousSelectionMode, { preserveSelection: false });
            this.previousSelectionMode = null;
        }
    }

    /**
     * 处理中心方向箭头的轴向拖拽。
     *
     * @param event 方向箭头管理器发布的拖拽阶段与标量。
     * @returns 无返回值。
     */
    private handleArrowDrag(event: PatternDirectionArrowAxisDragEvent): void {
        if (!this.isEditing
            || event.phase !== PatternDirectionArrowAxisDragPhase.Change
            || !event.contextKey.startsWith(SECTION_ARROW_DRAG_CONTEXT_PREFIX)) {
            return;
        }
        const rawIndex = Number(event.contextKey.slice(SECTION_ARROW_DRAG_CONTEXT_PREFIX.length));
        if (!Number.isInteger(rawIndex) || rawIndex < 0 || rawIndex > 2) {
            return;
        }
        this.setOffsetMillimeters(rawIndex, event.scalarValue);
    }

    /**
     * 处理 X/Y 双半圆旋转手柄发布的连续角度。
     *
     * @param event 旋转槽位、轴、阶段与角度。
     * @returns 无返回值；非编辑态及非 change 阶段安全忽略。
     */
    private handleRotationHandleDrag(event: SectionViewRotationHandleDragEvent): void {
        if (!this.isEditing || event.phase !== 'change') {
            return;
        }
        if (event.axis === 'x') {
            this.setRotationXDegrees(event.sectionIndex, event.angleDegrees);
            return;
        }
        this.setRotationYDegrees(event.sectionIndex, event.angleDegrees);
    }

    /**
     * 更新布尔型剖面字段。
     *
     * @param index 零基槽位。
     * @param field 可更新的布尔字段。
     * @param value 目标布尔值。
     * @returns 无返回值。
     */
    private updateSectionBoolean(
        index: number,
        field: 'reverse',
        value: boolean,
    ): void {
        this.assertEditing();
        this.sections[this.requireSectionIndex(index)][field] = value;
        this.syncAndPublish();
    }

    /**
     * 更新有限数字型剖面字段。
     *
     * @param index 零基槽位。
     * @param field 偏移或局部旋转字段。
     * @param value 目标有限数值。
     * @returns 无返回值。
     * @throws 值非有限数、槽位无效或不在编辑会话时抛出错误。
     */
    private updateSectionNumber(
        index: number,
        field: 'offsetMillimeters' | 'rotationXDegrees' | 'rotationYDegrees',
        value: number,
    ): void {
        this.assertEditing();
        if (!Number.isFinite(value)) {
            throw new Error(`剖面视图字段必须是有限数字: ${field}`);
        }
        const section = this.sections[this.requireSectionIndex(index)];
        section[field] = value;
        section.errorMessage = null;
        this.syncAndPublish();
    }

    /**
     * 根据当前状态更新裁剪、盖面、预览和箭头。
     *
     * 活动平面与颜色数组稳定复用，高频拖拽路径不创建临时数组或遍历闭包。
     *
     * @returns 无返回值。
     */
    private syncRendering(): void {
        this.activePlaneBuffer.length = 0;
        this.activeColorBuffer.length = 0;
        this.activePlaneSlots.fill(false);
        const sectionOneNormal = this.sections[0].reference?.normal ?? [0, 0, 1];

        if (this.isEnabled) {
            for (const section of this.sections) {
                if (!section.enabled || !section.reference) {
                    continue;
                }
                try {
                    const plane = this.sectionPlanes[section.index];
                    plane.updateFromDefinition({
                        reference: section.reference,
                        offsetMethod: this.offsetMethod,
                        offsetMillimeters: section.offsetMillimeters,
                        rotationXDegrees: section.rotationXDegrees,
                        rotationYDegrees: section.rotationYDegrees,
                        reverse: section.reverse,
                        selectedPlaneNormal: sectionOneNormal,
                    });
                    section.errorMessage = null;
                    this.activePlaneSlots[section.index] = true;
                    this.activePlaneBuffer.push(plane);
                    this.activeColorBuffer.push(section.color);
                } catch (error) {
                    section.errorMessage = error instanceof Error ? error.message : String(error);
                }
            }
        }

        this.renderPort.setPlanes(this.activePlaneBuffer, this.activeColorBuffer);
        this.renderPort.setCapEnabled(this.isEnabled && this.showCap);
        this.renderPort.setPreviewVisible(this.isEditing && this.isPreview);
        this.visibilityPolicy.setPlanes(this.activePlaneBuffer);
        this.syncPreviewArrows();
        this.syncPreviewRotationHandles();
        // 剖切与拾取数学状态已经同步完成；嵌套内容通知和手柄请求统一在宿主帧绘制。
        this.host.requestRender();
    }

    /**
     * 更新三个剖面的命名空间方向箭头。
     *
     * 原点使用固定三槽 Vector3 缓存，法向传递稳定 Plane.normal；
     * PatternDirectionArrowManager 在入口复制向量，因此不会持有后续可变的计算缓存。
     *
     * @returns 无返回值。
     */
    private syncPreviewArrows(): void {
        for (const index of SECTION_PLANE_INDEXES) {
            const slot = this.createArrowSlot(index);
            const section = this.sections[index];
            if (!this.isEditing || !this.isPreview || !this.activePlaneSlots[index] || !section.enabled) {
                if (this.previewArrowSlots.has(slot)) {
                    this.host.patternDirectionArrowManager.clearArrow(slot);
                    this.previewArrowSlots.delete(slot);
                }
                continue;
            }
            const plane = this.sectionPlanes[index];
            const anchorWorld = this.previewArrowAnchorBuffers[index];
            plane.copyOriginWorldTo(anchorWorld);
            const ringRadiusWorld = this.resolvePreviewGizmoRingRadiusWorld(anchorWorld);
            const bottomLength = ringRadiusWorld * SECTION_PREVIEW_GIZMO_WORLD_RATIOS.bottomLength;
            const topLength = ringRadiusWorld * SECTION_PREVIEW_GIZMO_WORLD_RATIOS.topLength;
            this.host.patternDirectionArrowManager.setArrow(slot, {
                color: Number.parseInt(section.color.slice(1), 16) || SECTION_PREVIEW_ARROW_COLOR_FALLBACK,
                anchorWorld,
                directionWorld: plane.normal,
                shaftRadius: ringRadiusWorld * SECTION_PREVIEW_GIZMO_WORLD_RATIOS.shaftRadius,
                shaftLength: bottomLength + topLength,
                headRadius: ringRadiusWorld * SECTION_PREVIEW_GIZMO_WORLD_RATIOS.headRadius,
                headLength: ringRadiusWorld * SECTION_PREVIEW_GIZMO_WORLD_RATIOS.headLength,
                bottomLength,
                topLength,
                anchorLift: 0,
                axisDrag: {
                    enabled: true,
                    contextKey: `${SECTION_ARROW_DRAG_CONTEXT_PREFIX}${index}`,
                    scalarValue: section.offsetMillimeters,
                    // 反向只翻转保留半空间，不移动平面；拖动轴随箭头翻转时需反转数值符号，
                    // 才能保证平面始终跟随指针，而不是朝箭头相反方向移动。
                    sceneUnitToScalarScale: section.reverse ? -1000 : 1000,
                },
            });
            this.previewArrowSlots.add(slot);
        }
    }

    /**
     * 更新三个剖面的 X/Y 双半圆旋转手柄。
     *
     * 最终显示坐标帧与顺序旋转的参数求值轴分开传递：前者保证 gizmo 整体相交，
     * 后者保留属性栏先 X 后 Y 的角度语义。全部向量写入固定缓存，拖拽同步不分配对象。
     *
     * @returns 无返回值。
     */
    private syncPreviewRotationHandles(): void {
        for (const index of SECTION_PLANE_INDEXES) {
            const section = this.sections[index];
            if (!this.isEditing || !this.isPreview || !this.activePlaneSlots[index] || !section.enabled) {
                if (this.previewRotationHandleSlots.has(index)) {
                    this.host.sectionViewRotationHandleManager.clearHandle(index);
                    this.previewRotationHandleSlots.delete(index);
                }
                continue;
            }
            const plane = this.sectionPlanes[index];
            plane.copyOriginWorldTo(this.previewArrowAnchorBuffers[index]);
            const ringRadiusWorld = this.resolvePreviewGizmoRingRadiusWorld(
                this.previewArrowAnchorBuffers[index],
            );
            plane.copyDisplayXAxisWorldTo(this.previewDisplayXAxisBuffers[index]);
            plane.copyDisplayYAxisWorldTo(this.previewDisplayYAxisBuffers[index]);
            plane.copyDisplayNormalWorldTo(this.previewDisplayNormalBuffers[index]);
            plane.copyRotationXAxisWorldTo(this.previewRotationXAxisBuffers[index]);
            plane.copyRotationYAxisWorldTo(this.previewRotationYAxisBuffers[index]);
            this.host.sectionViewRotationHandleManager.setHandle(index, {
                anchorWorld: this.previewArrowAnchorBuffers[index],
                displayXAxisWorld: this.previewDisplayXAxisBuffers[index],
                displayYAxisWorld: this.previewDisplayYAxisBuffers[index],
                displayNormalWorld: this.previewDisplayNormalBuffers[index],
                rotationXAxisWorld: this.previewRotationXAxisBuffers[index],
                rotationYAxisWorld: this.previewRotationYAxisBuffers[index],
                rotationXDegrees: section.rotationXDegrees,
                rotationYDegrees: section.rotationYDegrees,
                radiusWorld: ringRadiusWorld,
            });
            this.previewRotationHandleSlots.add(index);
        }
    }

    /**
     * 生成控制器专属箭头槽位。
     *
     * @param index 零基剖面槽位。
     * @returns 可与其它方向箭头功能隔离的稳定槽位名。
     */
    private createArrowSlot(index: SectionViewPlaneIndex): string {
        return `${SECTION_ARROW_SLOT_PREFIX}${index}`;
    }

    /**
     * 读取或首次标定当前会话的旋转环世界半径。
     *
     * 初始标定复用原有手柄可读性；数值一旦写入会话就不再读取相机，
     * 因此 zoom、resize 和投影切换只会改变正常场景投影，不会改写手柄世界尺寸。
     *
     * @param anchorWorld 首个活动剖面的世界中心。
     * @returns 当前会话的有限正世界半径。
     */
    private resolvePreviewGizmoRingRadiusWorld(anchorWorld: THREE.Vector3): number {
        if (this.previewGizmoRingRadiusWorld !== null) {
            return this.previewGizmoRingRadiusWorld;
        }
        const radiusWorld = this.host.sectionViewRotationHandleManager.resolveInitialWorldRadius(anchorWorld);
        if (!Number.isFinite(radiusWorld) || radiusWorld <= 0) {
            throw new Error('剖面手柄初始世界半径必须是有限正数');
        }
        this.previewGizmoRingRadiusWorld = radiusWorld;
        return radiusWorld;
    }

    /**
     * 同步渲染派生状态、登记普通重绘并立即发布状态通知。
     *
     * @returns 无返回值。
     */
    private syncAndPublish(): void {
        this.syncRendering();
        this.publishStateChanged();
    }

    /**
     * 发布状态变化；监听器异常不得阻断其他 UI 订阅。
     *
     * @returns 无返回值。
     */
    private publishStateChanged(): void {
        this.stateChangedListeners.forEach((listener) => {
            try {
                listener();
            } catch (error) {
                console.error('[SectionViewController] 状态监听器执行失败', error);
            }
        });
    }

    /**
     * 判断是否至少具有一个已启用且有参考的剖面。
     *
     * @returns 存在可参与裁剪的定义时返回 true。
     */
    private hasDefinition(): boolean {
        return this.sections.some((section) => section.enabled && section.reference !== null);
    }

    /**
     * 判断当前草稿能否确认。
     *
     * @returns 基础剖面有参考、槽位依赖合法且所有启用槽位均有参考时返回 true。
     */
    private canConfirm(): boolean {
        if (!this.sections[0].enabled || !this.sections[0].reference) {
            return false;
        }
        if (this.sections[2].enabled && !this.sections[1].enabled) {
            return false;
        }
        return this.sections.every((section) => !section.enabled || section.reference !== null);
    }

    /**
     * 校验并返回剖面槽位。
     *
     * @param index 外部传入的数字索引。
     * @returns 0、1 或 2 的精确槽位类型。
     * @throws 索引非整数或超出固定三槽范围时抛出错误。
     */
    private requireSectionIndex(index: number): SectionViewPlaneIndex {
        if (!Number.isInteger(index) || index < 0 || index > 2) {
            throw new Error(`剖面索引必须是 0、1 或 2: ${String(index)}`);
        }
        return index as SectionViewPlaneIndex;
    }

    /**
     * 断言当前处于编辑会话。
     *
     * @returns 无返回值。
     * @throws 控制器已释放或当前不在编辑会话时抛出错误。
     */
    private assertEditing(): void {
        this.assertNotDisposed();
        if (!this.isEditing) {
            throw new Error('剖面视图当前不处于编辑会话');
        }
    }

    /**
     * 断言控制器尚未释放。
     *
     * @returns 无返回值。
     * @throws 控制器已结束生命周期时抛出错误。
     */
    private assertNotDisposed(): void {
        if (this.isDisposed) {
            throw new Error('剖面视图控制器已释放');
        }
    }
}
